import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { toast } from 'react-toastify';

import { previewRemove, applyRemove } from 'api/rosterChangesApi';
import styles from './RosterRemovePreviewModal.module.css';

// The change-preview review modal (docs/role-and-deletion-design.html §5.6). Given a seed (athlete and/or user
// IDs), it previews the full cascade family-grouped, lets the admin review it, and applies it in one step. Laid
// out as a fixed-size region (fixed header / summary / footer + an internally-scrolling list) mirroring
// MemberSearchPicker, so the dialog never resizes. PIN is already validated by the caller (the bulk action is
// wrapped in PinProtectedAction).

const TYPE_LABEL = {
  Full: 'Member', // an athlete's "Full" membership reads as "Member" — consistent with the contact role badge
  Guest: 'Guest',
  Visitor: 'Visitor',
  Member: 'Member',
  Official: 'Official',
  Admin: 'Admin',
  Owner: 'Owner',
  Coach: 'Coach',
  Trialist: 'Trialist',
};

const typeBadgeClass = (type) => {
  if (type === 'Guest') return styles.guestBadge;
  if (type === 'Visitor') return styles.visitorBadge;
  return styles.memberBadge; // Full / Member / Official / Admin / Owner …
};

const TypeBadge = ({ type }) =>
  type ? (
    <span className={`${styles.typeBadge} ${typeBadgeClass(type)}`}>
      {TYPE_LABEL[type] || type}
    </span>
  ) : null;

TypeBadge.propTypes = { type: PropTypes.string };

// Builds the type-aware headline from the counts + the page's type context ("guest" / "visitor" / "").
const buildSummary = (memberCount, athleteCount, typeLabel) => {
  const prefix = typeLabel ? `${typeLabel} ` : '';
  const parts = [];
  if (memberCount > 0) {
    parts.push(`${memberCount} ${prefix}member${memberCount === 1 ? '' : 's'}`);
  }
  if (athleteCount > 0) {
    parts.push(`${athleteCount} ${prefix}athlete${athleteCount === 1 ? '' : 's'}`);
  }
  if (parts.length === 0) return 'Nothing will be removed.';
  // The results reassurance only applies when athletes are being removed, and agrees in number.
  const tail =
    athleteCount === 1
      ? ' Meet results for this athlete will not be deleted.'
      : athleteCount > 1
      ? ' Meet results for these athletes will not be deleted.'
      : '';
  return `You are about to remove ${parts.join(' and ')} from this club.${tail}`;
};

const RosterRemovePreviewModal = ({
  show,
  clubId,
  seed,
  title,
  typeLabel = '',
  onClose,
  onApplied,
}) => {
  const [phase, setPhase] = useState('loading'); // loading | review | applying | result | error
  const [plan, setPlan] = useState(null);
  const [result, setResult] = useState(null);
  const [errorMsg, setErrorMsg] = useState('');
  // Whether an apply succeeded this open — gates the deferred list refresh on close (see handleClose).
  const appliedRef = useRef(false);
  // The apply result, handed to onApplied on close so callers can react to WHO was removed (e.g. the member
  // details view navigates away if the viewed member was cascade-removed). The list pages ignore it.
  const lastResultRef = useRef(null);

  // Fire the dry-run preview when the modal opens for a seed.
  useEffect(() => {
    if (!show || !seed) return undefined;
    let cancelled = false;
    setPhase('loading');
    setPlan(null);
    setResult(null);
    setErrorMsg('');
    appliedRef.current = false;
    lastResultRef.current = null;
    previewRemove(clubId, seed)
      .then((res) => {
        if (!cancelled) {
          setPlan(res.data);
          setPhase('review');
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setErrorMsg(
            err?.response?.data?.message ||
              err?.response?.data ||
              err?.message ||
              'Could not analyze this change.'
          );
          setPhase('error');
        }
      });
    return () => {
      cancelled = true;
    };
  }, [show, clubId, seed]);

  const nameById = useMemo(() => {
    const map = {};
    (plan?.families || []).forEach((f) => {
      (f.contacts || []).forEach((c) => {
        map[c.id] = c.name;
      });
      (f.athletes || []).forEach((a) => {
        map[a.id] = a.name;
      });
    });
    (plan?.skipped || []).forEach((s) => {
      map[s.id] = s.name;
    });
    return map;
  }, [plan]);

  const handleConfirm = useCallback(async () => {
    setPhase('applying');
    try {
      const res = await applyRemove(clubId, seed);
      setResult(res.data);
      lastResultRef.current = res.data;
      setPhase('result');
      // Don't refresh the list yet — fetchMembers toggles the page's loading state, which unmounts/remounts this
      // modal (AdminTablePageLayout swaps children while loading) and would re-fire the preview against now-stale
      // data. Defer the refresh to close (handleClose) so the result view stays put.
      appliedRef.current = true;
    } catch (err) {
      const msg =
        err?.response?.data?.message ||
        err?.response?.data ||
        err?.message ||
        'The change could not be applied.';
      toast.error(typeof msg === 'string' ? msg : 'The change could not be applied.');
      setPhase('review');
    }
  }, [clubId, seed]);

  // Close, then run the deferred list refresh if an apply succeeded. onClose() runs first (show=false) so the
  // refresh's loading toggle can't remount the modal into a stale preview.
  const handleClose = useCallback(() => {
    const wasApplied = appliedRef.current;
    appliedRef.current = false;
    onClose();
    if (wasApplied) onApplied?.(lastResultRef.current);
  }, [onClose, onApplied]);

  if (!show) return null;

  const families = plan?.families || [];
  const skipped = plan?.skipped || [];
  const warnings = plan?.warnings || [];
  const nothingToRemove =
    (plan?.memberCount || 0) === 0 && (plan?.athleteCount || 0) === 0;

  const renderBody = () => {
    if (phase === 'loading') {
      return <div className={styles.center}>Analyzing this change…</div>;
    }
    if (phase === 'error') {
      return <div className={styles.center}>{errorMsg}</div>;
    }
    if (phase === 'applying') {
      return <div className={styles.center}>Removing…</div>;
    }
    if (phase === 'result' && result) {
      const succeeded = result.succeededCount || 0;
      const failed = result.failedCount || 0;
      const failures = (result.results || []).filter((r) => !r.success);

      // All good — a friendly, people-oriented confirmation with the member / athlete breakdown.
      if (failed === 0) {
        const prefix = typeLabel ? `${typeLabel} ` : '';
        const planCounts = result.planAsExecuted || {};
        const parts = [];
        if (planCounts.memberCount > 0) {
          parts.push(`${planCounts.memberCount} ${prefix}member${planCounts.memberCount === 1 ? '' : 's'}`);
        }
        if (planCounts.athleteCount > 0) {
          parts.push(`${planCounts.athleteCount} ${prefix}athlete${planCounts.athleteCount === 1 ? '' : 's'}`);
        }
        return (
          <p className={styles.summary}>
            <span className={styles.resultGood}>All done.</span>{' '}
            {parts.length > 0
              ? `Removed ${parts.join(' and ')} from the club.`
              : 'Nothing was removed.'}
          </p>
        );
      }

      // Some couldn't be removed — show what went through and list the rest to retry.
      const total = succeeded + failed;
      return (
        <p className={styles.summary}>
          Removed <span className={styles.resultGood}>{succeeded}</span> of {total}{' '}
          {total === 1 ? 'person' : 'people'}.{' '}
          <span className={styles.resultBad}>{failed} couldn’t be removed:</span>
          {failures.length > 0 && (
            <ul className={styles.failList}>
              {failures.map((f) => (
                <li key={f.id}>
                  <strong>{nameById[f.id] || f.id}</strong> — {f.reason || 'No reason given.'} (you can retry this one)
                </li>
              ))}
            </ul>
          )}
        </p>
      );
    }

    // review
    // A family renders as a group only when a contact is being removed (show just the removed contact(s) +
    // their athletes nested). A family whose contacts all stay contributes its athletes to a flat "contact
    // stays" list — so a staying contact is never shown as if it were being removed.
    const groups = [];
    const stayingAthletes = [];
    families.forEach((fam) => {
      const removedContacts = (fam.contacts || []).filter((c) => c.removed);
      if (removedContacts.length > 0) {
        groups.push({ contacts: removedContacts, athletes: fam.athletes || [] });
      } else {
        stayingAthletes.push(...(fam.athletes || []));
      }
    });

    return (
      <>
        <p className={styles.summary}>
          {buildSummary(plan?.memberCount || 0, plan?.athleteCount || 0, typeLabel)}
        </p>

        {warnings.length > 0 && (
          <div className={styles.warnBox}>
            {warnings.map((w, i) => (
              <div key={i}>{w}</div>
            ))}
          </div>
        )}

        <div className={styles.panel}>
          {groups.map((g, gi) => (
            <div key={g.contacts[0]?.id ?? gi} className={styles.family}>
              {g.contacts.map((c) => (
                <div key={c.id} className={styles.contactRow}>
                  <span className={styles.personBadge}>
                    {c.isSecondary ? 'Secondary' : 'Primary'}
                  </span>
                  <span className={styles.contactName}>{c.name}</span>
                  <TypeBadge type={c.role} />
                  {c.reason === 'no athletes left in this club' && (
                    <span className={styles.contactNote}>no athletes left</span>
                  )}
                </div>
              ))}
              {g.athletes.map((a) => (
                <div key={a.id} className={styles.athleteRow}>
                  <span className={styles.athleteBullet}>↳</span>
                  <span className={styles.personBadge}>Athlete</span>
                  <span className={styles.athleteName}>{a.name}</span>
                  <TypeBadge type={a.type} />
                </div>
              ))}
            </div>
          ))}

          {stayingAthletes.length > 0 && (
            <div className={styles.family}>
              {groups.length > 0 && (
                <div className={styles.skippedHeader}>Athletes (contact stays in the club)</div>
              )}
              {stayingAthletes.map((a) => (
                <div key={a.id} className={styles.contactRow}>
                  <span className={styles.personBadge}>Athlete</span>
                  <span className={styles.athleteName}>{a.name}</span>
                  <TypeBadge type={a.type} />
                </div>
              ))}
            </div>
          )}

          {skipped.length > 0 && (
            <div className={styles.skippedSection}>
              <div className={styles.skippedHeader}>Not removed</div>
              {skipped.map((s) => (
                <div key={`${s.entityType}-${s.id}`} className={styles.skippedRow}>
                  <span className={styles.athleteName}>{s.name}</span>
                  <span className={styles.contactNote}>{s.reason}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </>
    );
  };

  const renderFooter = () => {
    if (phase === 'result' || phase === 'error') {
      return (
        <button className={styles.btnPrimary} onClick={handleClose}>
          Close
        </button>
      );
    }
    const busy = phase === 'loading' || phase === 'applying';
    return (
      <>
        <button className={styles.btnSecondary} onClick={handleClose} disabled={phase === 'applying'}>
          Cancel
        </button>
        <button
          className={styles.btnDanger}
          onClick={handleConfirm}
          disabled={busy || nothingToRemove}
        >
          {phase === 'applying' ? 'Removing…' : 'Confirm removal'}
        </button>
      </>
    );
  };

  // No overlay onClick — dialogs must not close on an outside click (only the × / Cancel / Close).
  return (
    <div className={styles.overlay}>
      <div className={styles.content}>
        <div className={styles.header}>
          <h2 className={styles.title}>{title}</h2>
          <button className={styles.closeBtn} onClick={handleClose} aria-label="Close">
            &times;
          </button>
        </div>
        <div className={styles.body}>{renderBody()}</div>
        <div className={styles.footer}>{renderFooter()}</div>
      </div>
    </div>
  );
};

RosterRemovePreviewModal.propTypes = {
  show: PropTypes.bool.isRequired,
  clubId: PropTypes.string.isRequired,
  // The seed to remove: athlete profile IDs (athlete views) and/or user IDs (member views).
  seed: PropTypes.shape({
    athleteIds: PropTypes.arrayOf(PropTypes.string),
    userIds: PropTypes.arrayOf(PropTypes.string),
  }),
  title: PropTypes.string.isRequired,
  // Page type context for the summary copy: "guest" | "visitor" | "" (plain members).
  typeLabel: PropTypes.string,
  onClose: PropTypes.func.isRequired,
  onApplied: PropTypes.func,
};

export default RosterRemovePreviewModal;
